import { useMemo } from 'react'
import {
  queryOptions,
  useInfiniteQuery,
  useQueries,
  useQuery
} from '@tanstack/react-query'
import api from '../../config/axios-config.jsx'
import { projectQueryKeys } from '../constants.js'
import { DateTime } from 'luxon'

export const projectTreeItemTypeOptions = {
  FOLDER: 'Folder',
  PROJECT: 'Project'
}

export const projectTreeStateFilterOptions = {
  ALL: 'All',
  ACTIVE: 'Active',
  INACTIVE: 'Inactive'
}

export const projectTreeTypeFilterOptions = {
  ALL: 'All',
  FOLDERS: 'Folders'
}

export const projectTreeSortByOptions = {
  TITLE: 'Title',
  LAST_ACTIVITY: 'LastActivity'
}

export const projectTreeSortDirectionOptions = {
  ASC: 'ASC',
  DESC: 'DESC'
}

export const getProjectTreeChildren = async ({
  folderId = null,
  params = {},
  pageParam = 0
}) => {
  const {
    search = '',
    stateFilter = projectTreeStateFilterOptions.ACTIVE,
    typeFilter = projectTreeTypeFilterOptions.ALL,
    size = 50,
    sortBy = projectTreeSortByOptions.TITLE,
    sortDirection = projectTreeSortDirectionOptions.ASC
  } = params

  const timestamp = DateTime.now().toISO()
  const requestParams = {
    search,
    stateFilter,
    typeFilter,
    size,
    page: pageParam,
    sortBy,
    sortDirection,
    timestamp
  }

  if (folderId) {
    delete requestParams.search
  }

  const config = {
    method: 'get',
    params: requestParams
  }

  const { data } = await api(
    `/api/projects-api/folders${folderId ? `/${folderId}` : ''}/children`,
    config
  )

  return data.response ?? data
}

/**
 * Single-page variant of the project tree, shared by the route loader and the
 * company projects table. `useGetProjectTreeQuery` below stays the infinite
 * variant, used by the tile view and by the recursive folder list.
 */
export const projectTreePageQueryOptions = ({
  folderId = null,
  search = '',
  stateFilter = projectTreeStateFilterOptions.ACTIVE,
  typeFilter = projectTreeTypeFilterOptions.ALL,
  page = 0,
  size = 50,
  sortBy = projectTreeSortByOptions.TITLE,
  sortDirection = projectTreeSortDirectionOptions.ASC
} = {}) =>
  queryOptions({
    queryKey: [
      projectQueryKeys.GET_PROJECT_TREE_QUERY,
      'page',
      {
        folderId,
        search,
        stateFilter,
        typeFilter,
        page,
        size,
        sortBy,
        sortDirection
      }
    ],
    queryFn: () =>
      getProjectTreeChildren({
        folderId,
        params: {
          search,
          stateFilter,
          typeFilter,
          size,
          sortBy,
          sortDirection
        },
        pageParam: page
      }),
    retry: 0
  })

export const useProjectTreePageQuery = (params) =>
  useQuery(projectTreePageQueryOptions(params))

const normalizeProjectTreeQueryArgs = (queryArgs, paramsArg, optionsArg) => {
  if (queryArgs && typeof queryArgs === 'object' && !Array.isArray(queryArgs)) {
    return queryArgs
  }

  return {
    folderId: queryArgs ?? null,
    params: paramsArg ?? {},
    options: optionsArg ?? {}
  }
}

export const useGetProjectTreeQuery = (queryArgs, paramsArg, optionsArg) => {
  const {
    folderId = null,
    params = {},
    options = {}
  } = normalizeProjectTreeQueryArgs(queryArgs, paramsArg, optionsArg)

  const {
    search = '',
    stateFilter = projectTreeStateFilterOptions.ACTIVE,
    typeFilter = projectTreeTypeFilterOptions.ALL,
    size = 50,
    page = 0,
    sortBy = projectTreeSortByOptions.TITLE,
    sortDirection = projectTreeSortDirectionOptions.ASC
  } = params

  const handleQuery = async ({ pageParam = page }) => {
    return getProjectTreeChildren({
      folderId,
      params: {
        search,
        stateFilter,
        typeFilter,
        size,
        page,
        sortBy,
        sortDirection
      },
      pageParam
    })
  }

  return useInfiniteQuery({
    queryKey: [
      projectQueryKeys.GET_PROJECT_TREE_QUERY,
      folderId ?? null,
      search,
      stateFilter,
      typeFilter,
      size,
      page,
      sortBy,
      sortDirection
    ],
    queryFn: handleQuery,
    retry: 0,
    initialPageParam: page,
    getNextPageParam: (lastPage, _pages, lastPageParam) => {
      if (lastPage.last === true) {
        return undefined
      }
      return lastPageParam + 1
    },
    ...options
  })
}

export const useGetProjectTreeChildrenQueries = ({
  folderIds = [],
  params = {},
  options = {}
} = {}) => {
  const {
    stateFilter = projectTreeStateFilterOptions.ACTIVE,
    typeFilter = projectTreeTypeFilterOptions.ALL,
    size = 50,
    sortBy = projectTreeSortByOptions.TITLE,
    sortDirection = projectTreeSortDirectionOptions.ASC
  } = params

  const folderChildrenQueries = useQueries({
    queries: folderIds.map((folderId) => ({
      queryKey: [
        projectQueryKeys.GET_PROJECT_TREE_QUERY,
        'folderChildren',
        folderId,
        stateFilter,
        typeFilter,
        size,
        sortBy,
        sortDirection
      ],
      queryFn: () =>
        getProjectTreeChildren({
          folderId,
          params: {
            stateFilter,
            typeFilter,
            size,
            sortBy,
            sortDirection
          }
        }),
      retry: 0,
      enabled: !!folderId,
      ...options
    }))
  })

  const folderChildrenStateById = useMemo(() => {
    const childrenState = new Map()

    folderIds.forEach((folderId, index) => {
      childrenState.set(folderId, folderChildrenQueries[index])
    })

    return childrenState
  }, [folderIds, folderChildrenQueries])

  const childrenByFolderId = useMemo(() => {
    const children = new Map()

    folderChildrenStateById.forEach((query, folderId) => {
      if (query?.data?.content) {
        children.set(folderId, query.data.content)
      }
    })

    return children
  }, [folderChildrenStateById])

  return {
    folderChildrenQueries,
    folderChildrenStateById,
    childrenByFolderId
  }
}
